// Visual data structure (V2 format - object, V1 format - this is JSON.stringify'd)
export interface RecommendationVisual {
  plot_title: string;
  labels: string[];
  datasets: Array<{
    label: string;
    unit: string;
    data: number[];
  }>;
  revenue_pct_diff?: number;
}

// V2 Table item types
export interface ExpandTableItemV2 {
  rec_detail_id: string;
  unit_fk: string;
  unit_label: string;
  item_category: string;
  item_name: string;
  stations_offering_item: string;
  daily_qty_sold: number;
  daily_revenue: number;
  site_avg_daily_revenue: number;
  site_avg_daily_qty_sold: number;
  potential_additional_qty: number;
  potential_additional_sales: number;
}

export interface ReduceTableItemV2 {
  rec_detail_id: string;
  unit_fk: string;
  unit_label: string;
  item_category: string;
  item_name: string;
  stations_offering_item: string;
  daily_qty_sold: number;
  daily_revenue: number;
  site_avg_daily_revenue: number;
  site_avg_daily_qty_sold: number;
  revenue_conservation: number;
}

// Unified Recommendation type that supports both V1 and V2 responses
export interface Recommendation {
  // ID field - V1 uses recommendation_id, V2 uses rec_id
  recommendation_id?: string;
  rec_id?: string;
  // Common fields
  start_date: string;
  end_date: string;
  rec_type: string;
  url: string | null;
  timeframe_subfilter: string;
  // Visual - V1 is JSON string, V2 is object
  visual: string | RecommendationVisual;
  // Table data - only present in V2
  table?: ExpandTableItemV2[] | ReduceTableItemV2[];
}

// Chart types for rendering
export interface ChartDataset {
  label: string;
  data: number[];
  backgroundColor: string;
  borderRadius: number;
  borderSkipped: string;
  yAxisID: string;
  unit: string;
}

export interface ChartData {
  labels: string[];
  datasets: ChartDataset[];
}

export interface ChartOptions {
  maintainAspectRatio: boolean;
  aspectRatio: number;
  maxBarThickness: number;
  layout: {
    padding: {
      top: number;
    };
  };
  plugins: {
    legend: {
      display: boolean;
      position: string;
      labels: {
        boxWidth: number;
        boxHeight: number;
        padding: number;
        font: {
          size: number;
        };
        usePointStyle: boolean;
        pointStyle: string;
      };
    };
    datalabels: {
      anchor: string;
      align: string;
      font: {
        size: number;
        weight: string;
      };
      formatter: (value: number, context: any) => string | number;
    };
  };
  scales: {
    y: {
      display: boolean;
    };
    y1: {
      display: boolean;
    };
    x: {
      grid: {
        display: boolean;
      };
      border: {
        display: boolean;
      };
      ticks: {
        font: {
          size: number;
        };
        maxRotation: number;
        minRotation: number;
        callback: (value: any, index: number) => string | string[];
      };
    };
  };
}

export enum RecommendationType {
  CPG = 'cpg',
  EXPAND = 'expand',
  REDUCE = 'reduce',
}

// Extended recommendation with computed/derived fields for UI
export interface RecommendationWithDetails extends Recommendation {
  recommendationType?: RecommendationType;
}

// description recommendations surfaced by the CI team's config-recs API.
export interface DescriptionRecommendation {
  item_id: string;
  item_name: string;
  brand_id: string;
  brand_name: string;
  menu_names: string[];
  generated_description: string;
}
